import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import AdjustMapView from './AdjustMapView';
import PropTypes from 'prop-types';
import { fetchDeviceReading } from 'store/actions/widgetActions';
import { useDispatch, useSelector } from 'react-redux';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button } from '@mui/material';
import { useState } from 'react';
import ChartModal from '../ChartModal';

const MarkerIcon = L.icon({
  iconUrl: '/assets/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const MapChart = ({ charts, title }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { deviceDetails } = useSelector((state: any) => state.widget);
  const axesMap = charts?.axes?.reduce((map, axis) => {
    map[axis.axis_col] = axis.axis_label;
    return map;
  }, {});

  const getDeviceReading = (deviceId) => {
    dispatch({ type: fetchDeviceReading.request, payload: { deviceId } });
    setModalOpen(true);
  };

  const locations = charts?.data?.map((data) => {
    const latitudeKey = charts?.axes?.find((axis) => axis.axis_type === 'x_axis')?.axis_col;
    const longitudeKey = charts?.axes?.find((axis) => axis.axis_type === 'y_axis')?.axis_col;

    return {
      latitude: data[latitudeKey],
      longitude: data[longitudeKey],
    };
  });

  const titleStyle = {
    marginTop: '0',
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontWeight: '900',
    fontSize: '14px',
    color: '#373d3f',
  };

  const getCategories = () => {
    const categories = deviceDetails?.map((data) => data.timestamp);
    const uniqueCategories = Array.from(new Set(categories));
    return { categories: uniqueCategories };
  };

  const getChartOptions = () => {
    const { categories }: { categories: any } = getCategories();

    const chartOptions = {
      chart: {
        type: 'line',
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        stacked: false,
      },
      xaxis: {
        categories: categories,
        type: 'datetime',
        title: {
          text: 'Date',
        },
      },
      yaxis: {
        title: {
          text: 'Reading',
        },
      },
    };
    return chartOptions;
  };

  const chartSeries = [
    {
      name: deviceDetails[0]?.label || 'Value',
      data: deviceDetails.map((reading) => reading.value.toFixed(2)),
    },
  ];

  return (
    <>
      {title && <p style={titleStyle}>{title}</p>}
      <div id='map-chart-container' style={{ height: '100%', width: '100%' }}>
        <MapContainer
          center={[0, 0]}
          zoom={1}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <AdjustMapView locations={locations || []} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
          {charts?.data?.map((data) => (
            <Marker
              key={data.device_key}
              position={[data.latitude, data.longitude]}
              icon={MarkerIcon}
            >
              <Popup>
                {Object.entries(data).map(([key, value]) => (
                  <p key={key}>{axesMap[key] ? `${axesMap[key]}: ${value}` : ''}</p>
                ))}
                <div style={{ textAlign: 'right', marginTop: '10px' }}>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => getDeviceReading(data.device_key)}
                    startIcon={<InfoOutlinedIcon />}
                  >
                    View Details
                  </Button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {deviceDetails && deviceDetails.length > 0 && (
        <ChartModal
          open={modalOpen}
          handleClose={() => setModalOpen(false)}
          modalChartOptions={getChartOptions()}
          type='line'
          seriesData={chartSeries}
          categories={getCategories()}
        />
      )}
    </>
  );
};

MapChart.propTypes = {
  charts: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
};

export default MapChart;
